import { useCallback, useEffect, useRef, useState } from "react";
import { demoApi } from "@/api/client";
import { describeReadFailure } from "@/lib/apiFailure";
import type { CreateMemoRequest, Guid, Memo } from "@/api/types";

export type MemosStatus = "loading" | "ready" | "error";

/** The Home card's own page — a real server-side page (`?limit=5`), never a
 *  client-side slice of a larger response (build brief). */
const HOME_MEMOS_LIMIT = 5;

export interface UseMemosResult {
  /** Null until the request resolves, or if it failed. */
  memos: Memo[] | null;
  status: MemosStatus;
  error: string | null;
  /** POST, then re-fetches the list — ordering is server-side
   *  (`memo_date IS NULL ASC, memo_date ASC, created_at ASC`), so a re-fetch is the only
   *  way to place the new row correctly rather than guessing where to splice it in.
   *  Rethrows on failure; the list/status are left untouched so the caller can show an
   *  inline form error without disturbing what is already on screen. */
  createMemo: (input: CreateMemoRequest) => Promise<void>;
  /** POST .../archive, then re-fetches the list. Rethrows on failure, same posture as
   *  `createMemo` above. */
  archiveMemo: (id: Guid) => Promise<void>;
}

/**
 * Fetches the tenant's own short running notes from GET /api/demo/home/memos?limit=5 —
 * ef-051-1. Deliberately a copy of useCashFlowPlanner's load/cancel/AbortController
 * shape rather than a shared generic (same convention that hook's own remarks state for
 * useProfitAndLoss/useBalanceSheet).
 *
 * A "ready" result with `memos` empty is a successful answer meaning no memos exist yet
 * — the caller renders the honest empty state (MemosCard.tsx), never a spinner.
 */
export function useMemos(): UseMemosResult {
  const [memos, setMemos] = useState<Memo[] | null>(null);
  const [status, setStatus] = useState<MemosStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  // Cancels a still-in-flight load when a NEWER one starts (create/archive both trigger
  // one), same reasoning useSelectedEntity/usePendingTime's own de-dupe comments give for
  // not letting an older response land after a newer one.
  const controllerRef = useRef<AbortController | null>(null);

  const load = useCallback(async (): Promise<void> => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setStatus("loading");
    setError(null);
    try {
      const result = await demoApi.getMemos(HOME_MEMOS_LIMIT, controller.signal);
      setMemos(result);
      setStatus("ready");
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setMemos(null);
      setStatus("error");
      setError(describeReadFailure(err));
    }
  }, []);

  useEffect(() => {
    void load();
    return () => controllerRef.current?.abort();
  }, [load]);

  const createMemo = useCallback(
    async (input: CreateMemoRequest): Promise<void> => {
      await demoApi.createMemo(input);
      await load();
    },
    [load],
  );

  const archiveMemo = useCallback(
    async (id: Guid): Promise<void> => {
      await demoApi.archiveMemo(id);
      await load();
    },
    [load],
  );

  return { memos, status, error, createMemo, archiveMemo };
}
